import { useState } from "react";
import { useParams } from "react-router-dom";
import { UserPlus, MoreHorizontal, LogIn, AlertTriangle } from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsField } from "@/components/v2/settings-field";
import { SettingsTable } from "@/components/v2/settings-table";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAllAccounts,
  useAccountUsers,
  useAdminResendInvite,
  useAdminForceLink,
  useAdminResetPassword,
  useAdminSuspendUser,
  useAdminDeleteUser,
  useAdminUpdateUserRole,
  useAdminRemoveUser,
  useAdminInviteUser,
  useLookupAuthUserByEmail,
  type AccountUserOnAccount,
} from "@/hooks/use-admin";
import { useImpersonation } from "@/hooks/use-impersonation";
import { formatRole, formatRelativeDate } from "@/lib/format-utils";

// ─── EditRoleSheet ────────────────────────────────────────────────────────────

interface EditRoleSheetProps {
  open: boolean;
  onClose: () => void;
  user: AccountUserOnAccount;
}

function EditRoleSheet({ open, onClose, user }: EditRoleSheetProps) {
  const [role, setRole] = useState<string>(user.account_role);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateRole = useAdminUpdateUserRole();

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      await updateRole.mutateAsync({
        userId: user.id,
        account_role: role,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[400px] sm:max-w-[400px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Edit Role</SheetTitle>
        </SheetHeader>
        <form
          id="edit-role-form"
          className="flex-1 py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <p className="typo-body text-[color:var(--color-text-md)]">
            {user.email}
          </p>
          <SettingsField label="Account Role">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </SettingsField>
          {error && <p className="typo-body text-destructive">{error}</p>}
        </form>
        <FormSheetFooter
          onCancel={handleClose}
          submitLabel="Save"
          isSubmitting={submitting}
          formId="edit-role-form"
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── InviteUserSheet ──────────────────────────────────────────────────────────

interface InviteUserSheetProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountBusinessIds?: string[];
}

function InviteUserSheet({ open, onClose, accountId }: InviteUserSheetProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("viewer");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = useLookupAuthUserByEmail(email);
  const inviteUser = useAdminInviteUser();

  function handleClose() {
    setEmail("");
    setFirstName("");
    setLastName("");
    setRole("viewer");
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      await inviteUser.mutateAsync({
        accountId,
        data: {
          email,
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          account_role: role,
          force_link_existing_auth_user_id:
            lookup.data?.authUser?.id ?? undefined,
        },
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invite user");
    } finally {
      setSubmitting(false);
    }
  }

  const existingUser = lookup.data?.authUser;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Invite User</SheetTitle>
        </SheetHeader>

        <form
          id="invite-user-form"
          className="flex-1 overflow-y-auto py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <SettingsField label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </SettingsField>

          {existingUser && (
            <div className="flex items-start gap-3 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-sm">
              <LogIn className="mt-0.5 h-4 w-4 flex-shrink-0 text-[color:var(--color-text-md)]" />
              <div>
                <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                  Auth user found
                </p>
                <p className="typo-body text-[color:var(--color-text-md)]">
                  {existingUser.email} — force-link will be applied
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <SettingsField label="First Name">
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
              />
            </SettingsField>
            <SettingsField label="Last Name">
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
              />
            </SettingsField>
          </div>

          <SettingsField label="Role">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="billing">Billing</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </SettingsField>

          {error && <p className="typo-body text-destructive">{error}</p>}
        </form>

        <FormSheetFooter
          onCancel={handleClose}
          submitLabel="Send Invite"
          isSubmitting={submitting || !email.trim()}
          formId="invite-user-form"
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── Row Action Menu ──────────────────────────────────────────────────────────

interface PendingConfirm {
  title: string;
  description: string;
  onConfirm: () => void;
}

function UserActionMenu({ user }: { user: AccountUserOnAccount }) {
  const resend = useAdminResendInvite();
  const forceLink = useAdminForceLink();
  const resetPassword = useAdminResetPassword();
  const suspend = useAdminSuspendUser();
  const deleteUser = useAdminDeleteUser();
  const remove = useAdminRemoveUser();
  const { startImpersonation } = useImpersonation();
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const canResend = user.status === "invited";
  const canImpersonate = user.businesses[0]?.business_id != null;

  async function handleAction(
    action: "resend" | "force-link" | "reset-password",
  ) {
    try {
      if (action === "resend") await resend.mutateAsync(user.id);
      else if (action === "force-link") await forceLink.mutateAsync(user.id);
      else if (action === "reset-password")
        await resetPassword.mutateAsync(user.id);
    } catch {
      // Errors surface via toast in a future pass
    }
  }

  function handleImpersonate() {
    const businessId = user.businesses[0]?.business_id;
    if (!businessId) return;
    startImpersonation(businessId);
    window.location.href = "/dashboard";
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label="User actions"
            className="h-7 w-7 p-0"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-52 bg-[color:var(--color-surface)] shadow-md border-[color:var(--color-border)]"
        >
          <DropdownMenuItem
            disabled={!canImpersonate}
            onClick={handleImpersonate}
          >
            <LogIn className="mr-2 h-3.5 w-3.5" />
            Impersonate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={!canResend}
            onClick={() => handleAction("resend")}
          >
            Resend invite
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleAction("reset-password")}>
            Reset password
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleAction("force-link")}>
            Force-link to auth user
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setEditRoleOpen(true)}>
            Edit role
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() =>
              setPending({
                title: "Remove user?",
                description: `Remove ${user.email} from this account? They will lose all access.`,
                onConfirm: () => remove.mutate(user.id),
              })
            }
          >
            Remove from account
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() =>
              setPending({
                title: "Suspend user?",
                description: `Suspend ${user.email}? They will lose access to this account.`,
                onConfirm: () => suspend.mutate(user.id),
              })
            }
          >
            Suspend
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() =>
              setPending({
                title: "Delete user?",
                description: `Permanently delete ${user.email}? This cannot be undone.`,
                onConfirm: () => deleteUser.mutate(user.id),
              })
            }
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {editRoleOpen && (
        <EditRoleSheet
          open={editRoleOpen}
          onClose={() => setEditRoleOpen(false)}
          user={user}
        />
      )}

      <ConfirmDialog
        open={!!pending}
        onOpenChange={(open) => !open && setPending(null)}
        title={pending?.title ?? ""}
        description={pending?.description ?? ""}
        confirmLabel="Confirm"
        onConfirm={() => {
          pending?.onConfirm();
          setPending(null);
        }}
      />
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function V2AdvaAccountUsersPage() {
  const { id: accountId } = useParams<{ id: string }>();
  const { data: accountsData } = useAllAccounts();
  const account = accountsData?.accounts.find((a) => a.id === accountId);
  const { data, isLoading } = useAccountUsers(accountId);
  const [inviteOpen, setInviteOpen] = useState(false);

  const users = data?.users ?? [];

  return (
    <SettingsPage
      breadcrumbs={[
        { label: "Adva" },
        { label: "Accounts", href: "/settings/adva/accounts" },
        { label: account?.name ?? "Account" },
        { label: "Users" },
      ]}
      title={account ? `${account.name} — Users` : "Users"}
      subtitle="All users on this account."
      actions={
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus className="h-4 w-4 mr-1.5" />
          Invite User
        </Button>
      }
    >
      <SettingsTable
        colSpan={6}
        isLoading={isLoading}
        isEmpty={users.length === 0}
        emptyMessage="No users on this account. Invite the first user above."
      >
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Name
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Role
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Status
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden md:table-cell">
              Businesses
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden lg:table-cell">
              Last Sign-in
            </TableHead>
            <TableHead className="w-12 px-4" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const name = [user.first_name, user.last_name]
              .filter(Boolean)
              .join(" ");
            return (
              <TableRow
                key={user.id}
                className="h-9 border-b border-[color:var(--color-border)] last:border-0"
              >
                <TableCell className="px-4 py-1.5">
                  <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                    {name || user.email}
                  </p>
                  {name && (
                    <p className="typo-body text-xs text-[color:var(--color-text-md)]">
                      {user.email}
                    </p>
                  )}
                </TableCell>
                <TableCell className="px-4 py-1.5">
                  <Badge variant="secondary">
                    {formatRole(user.account_role)}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-1.5">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={user.status === "active" ? "default" : "outline"}
                    >
                      {user.status}
                    </Badge>
                    {user.is_stale_invite && (
                      <Badge
                        variant="outline"
                        className="gap-1"
                        title="Invite older than 7 days and not yet accepted"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Stale
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="px-4 py-1.5 hidden md:table-cell typo-body text-xs text-[color:var(--color-text-md)]">
                  {user.all_businesses_access ? (
                    <span className="italic text-[color:var(--color-text-md)]">
                      All businesses
                    </span>
                  ) : user.businesses.length === 0 ? (
                    <span className="text-[color:var(--color-text-md)]">—</span>
                  ) : (
                    user.businesses
                      .map((b) => b.business_name ?? b.business_id.slice(0, 8))
                      .join(", ")
                  )}
                </TableCell>
                <TableCell className="px-4 py-1.5 hidden lg:table-cell typo-body text-xs text-[color:var(--color-text-md)]">
                  {formatRelativeDate(user.last_sign_in_at)}
                </TableCell>
                <TableCell className="px-4 py-1.5 text-right">
                  <UserActionMenu user={user} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </SettingsTable>

      <InviteUserSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        accountId={accountId ?? ""}
      />
    </SettingsPage>
  );
}
